<template>
  <v-container>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      :position="mobile ? 'bottom' : 'top'"
    >
      {{ snackbarText }}

      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="snackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <div class="d-flex align-center mb-4">
      <h1 class="text-h3">Delayed Shipments</h1>

    </div>

    <shipment-search-form
        v-if="!loading && !error"
        :initial-values="searchParams"
        @search="handleSearch"
      />
    <!-- Loading State -->
    <v-card v-if="loading">
      <v-card-text>
        <div class="d-flex align-center justify-center pa-4">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </v-card-text>
    </v-card>

    <!-- Error State -->
    <v-alert
      v-else-if="error"
      type="error"
      class="mb-4"
    >
      {{ error }}
    </v-alert>

    <!-- Content -->
    <template v-else>
        <v-card class="mb-4">
            <v-card-title class="d-flex align-center">
              <v-checkbox
                v-model="selectAll"
                @update:model-value="toggleSelectAll"
                label="Select All"
                hide-details
                density="compact"
                class="mr-4"
              ></v-checkbox>
              
              <v-spacer></v-spacer>
              <v-btn
                color="error"
                :disabled="selectedSales.length === 0"
                :loading="ignoring"
                class="mr-4"
                prepend-icon="mdi-delete"
                @click="ignoreSales"
              >
                Ignore ({{ selectedSales.length }})
              </v-btn>
              <v-btn
                color="primary"
                :loading="exporting"
                class="mr-4"
                prepend-icon="mdi-file-export"
                @click="exportSales"
              >
                Export All
              </v-btn>
            </v-card-title>
          </v-card>
      <delayed-sale-item
        v-for="sale in sales"
        :key="sale.ID"
        :sale="sale"
        :selected="selectedSales.includes(sale.ID)"
        @selection-changed="handleSelectionChanged"
        @send-message="openMessageDialog(sale)"
      />

      <!-- Pagination -->
      <div class="d-flex justify-center mt-4">
        <v-pagination
          v-model="currentPage"
          :length="totalPages"
          :total-visible="7"
        ></v-pagination>
      </div>
    </template>

    <!-- Message Dialog -->
    <v-dialog v-model="showMessageDialog" max-width="700px">
      <v-card>
        <v-card-title>Send Message</v-card-title>
        <v-card-text>
          <send-email-form
            v-if="selectedSale"
            :to="selectedSale.BuyerEmail"
            :subject="'[Important] Your Order is Delayed by the Carrier'"
            :message="generateMessage(selectedSale)"
            :loading="sending"
            @submit="handleSendMessage"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import ShipmentSearchForm from '@/components/sales/ShipmentSearchForm.vue'
import DelayedSaleItem from '@/components/sales/DelayedSaleItem.vue'
import SendEmailForm from '@/components/common/SendEmailForm.vue'
import { salesService } from '@/services/salesService'

const loading = ref(false)
const error = ref(null)
const ignoring = ref(false)
const sales = ref([])
const currentPage = ref(1)
const totalPages = ref(1)
const selectedSales = ref([])
const searchParams = ref([])
const selectAll = ref(false)
const showMessageDialog = ref(false)
const selectedSale = ref(null)
const sending = ref(false)


const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('error')

const fetchSales = async (page = 1, params = {}) => {
  loading.value = true
  error.value = null
  try {
    const response = await salesService.getDelayedShipments(page, params)
    sales.value = response.data
    totalPages.value = response.total_pages
  } catch (err) {
    console.error('Error fetching delayed shipments:', err)
    error.value = 'Failed to load delayed shipments'
  } finally {
    loading.value = false
  }
}

const handleSearch = (params) => {
  searchParams.value = Object.entries(params)
  currentPage.value = 1
  fetchSales(1, {
    filters: searchParams.value.map(([key, value]) => `${key}:${value}`).join(','),
    page: currentPage.value,
  })
}

const handleSelectionChanged = (saleId) => {
  const index = selectedSales.value.indexOf(saleId)
  if (index === -1) {
    selectedSales.value.push(saleId)
  } else {
    selectedSales.value.splice(index, 1)
  }
}

const toggleSelectAll = () => {
  if (selectAll.value) {
    selectedSales.value = sales.value.map(sale => sale.ID)
  } else {
    selectedSales.value = []
  }
}

const exportSales = async () => {
  try {
    const blob = await salesService.exportDelayedShipments(selectedSales.value)
    
    // Create download link
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `delayed-shipments-${new Date().toISOString().split('T')[0]}.csv`)
    
    // Trigger download
    document.body.appendChild(link)
    link.click()
    
    // Cleanup
    link.parentNode.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (err) {
    console.error('Error exporting sales:', err)
    error.value = 'Failed to export sales'
  }
}

const ignoreSales = async () => {
  if (selectedSales.value.length === 0) return
  
  ignoring.value = true
  try {
    // Process all selected sales
    await Promise.all(
      selectedSales.value.map(id => 
        salesService.updateTracking(id, 'Ignored')
      )
    )
    
    // Refresh the list
    await fetchSales(currentPage.value, Object.fromEntries(searchParams.value))
    
    // Clear selection
    selectedSales.value = []
    selectAll.value = false
  } catch (err) {
    console.error('Error ignoring sales:', err)
    error.value = 'Failed to ignore sales'
  } finally {
    ignoring.value = false
  }
}

const generateMessage = (sale) => {
  return `Hello

Thank you again for your order of ${sale.SKU} - ${sale.itemtitle}

${sale.PONumber}
Tracking Information
Tracking Number: ${sale.trackingnumber}
Your order has shipped from our warehouse on time but it appears that the carrier has delayed the order from our warehouse to you. My sincerest apologies that this has happened. We put our trust into the carriers that they can deliver these shipments on time but once shipped the package is out of our hands for delivery.
I do show that they are still showing the package will be delivered just not within the time frame originally posted. We are very sorry about this and will be contacting the carrier so this does not continue to happen. In the meantime would you like to keep the order and receive a 10% discount for the delay or cancel the order and receive a full refund? Please let us know by simply replying to this message so we can get this resolved for you quickly.


Thank you,
Customer Support`
}

const handleSendMessage = async (formData) => {
  sending.value = true
  snackbar.value = false
  try {
    await salesService.sendMessage(selectedSale.value.ID, formData)
    showMessageDialog.value = false
    snackbarText.value = `Successfully sent message to ${selectedSale.value.BuyerEmail}`
    snackbarColor.value = 'success'
    snackbar.value = true
  } catch (err) {
    console.error('Error sending message:', err)
    snackbarText.value = 'Failed to send message'
    snackbarColor.value = 'error'
    snackbar.value = true
  } finally {
    sending.value = false
  }
}

const openMessageDialog = (sale) => {
  selectedSale.value = sale
  showMessageDialog.value = true
}

watch(currentPage, () => {
  fetchSales(currentPage.value, Object.fromEntries(searchParams.value))
})

onMounted(() => {
  fetchSales()
})
</script> 